import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { api, type ApiRequestConfig } from 'api/v1/api';
import { getMyResponses } from 'api/v1/requests/bulletins';
import { isAuthSessionActive, useAuthSession } from 'hooks/useAuthSession';
import { addNotification } from 'notifications';
import { useChatWebSocket } from 'modules/ChatV2/store/ChatWebSocketContext';

const SYNC_KEY = ['tutor-bulletins-realtime-sync'];

/**
 * Для репетитора: подтягивает одобренные заявки и отклики без перезагрузки страницы.
 */
export const useRealtimeTutorBulletinsSync = (enabled = true) => {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { data: session } = useAuthSession();
  const isTutor = session?.roles?.includes(1);
  const isEnabled =
    enabled &&
    isAuthSessionActive(session) &&
    isTutor &&
    (pathname.includes('/teacher') || pathname.includes('/chat'));
  const { subscribeGlobal } = useChatWebSocket();

  useEffect(() => {
    if (!isEnabled) return;
    
    const unsubscribe = subscribeGlobal((event) => {
      if (['notification', 'bulletin_sync'].includes(event.type)) {
        void queryClient.invalidateQueries({ queryKey: SYNC_KEY });
        void queryClient.invalidateQueries({ queryKey: ['bulletins'] });
        void queryClient.invalidateQueries({ queryKey: ['bulletinPublic'] });
      }
      if (event.type === 'notification' && event.data?.notification_type === 'bulletin_available') {
        const rawId = event.data.bulletin_id;
        const bulletinId = rawId != null ? Number(rawId) : 0;
        const href =
          bulletinId > 0 ? `/teacher/search?focus=${bulletinId}` : '/teacher/search';
        addNotification('Появилась новая заявка', 'info', { href });
        if (pathname.startsWith('/teacher/search') && bulletinId > 0) {
          navigate(href, { replace: true });
        }
      }
    });
    
    return () => unsubscribe();
  }, [isEnabled, subscribeGlobal, queryClient, pathname, navigate]);

  return useQuery({
    queryKey: SYNC_KEY,
    queryFn: async () => {
      const [responses, notifications] = await Promise.all([
        getMyResponses().catch(() => null),
        api
          .get('/notifications', { skipGlobalErrorHandler: true } as ApiRequestConfig)
          .then((response) => response.data)
          .catch(() => null),
      ]);

      const prevResponses = queryClient.getQueryData<{ items?: Array<{ bulletin_id: number; status?: string }> }>([
        'my_responses',
        'all',
      ]);

      if (responses?.items) {
        queryClient.setQueryData(['my_responses', 'all'], responses);
        const prevIds = new Set((prevResponses?.items ?? []).map((r) => r.bulletin_id));
        const newlyApproved = responses.items.filter(
          (item) =>
            !prevIds.has(item.bulletin_id) &&
            (item.status === 'accepted' || item.status === 'pending'),
        );
        // При первой загрузке кэша еще нет: все отклики выглядели бы новыми и тост всплывал на каждой странице.
        if (prevResponses && newlyApproved.length > 0) {
          addNotification('Появились новые заявки для переписки', 'info');
        }
      }

      void queryClient.invalidateQueries({ queryKey: ['bulletins'] });
      void queryClient.invalidateQueries({ queryKey: ['chat'] });

      return { responses, notifications };
    },
    enabled: isEnabled,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });
};
